<template>
  <div class="new-document-page">
    <nav class="breadcrumb mt-5" aria-label="breadcrumbs">
      <ul>
        <li><router-link to="/dashboard">Dashboard</router-link></li>
        <li class="is-active">
          <router-link to="/dashboard/create-document" aria-current="true">
            Create Document
          </router-link>
        </li>
      </ul>
    </nav>

    <div>
      <div class="column is-12 mb-2">
        <h1 class="title">Create E-Invoice Management</h1>
      </div>
      <div class="columns is-fullheight">
        <div class="column is-5 mt-5">
          <div class="left-half">
            <CreateOrEditInvoice />
          </div>
          <div class="left-half">
            <UploadCSV @loading="toggleLoading" @response="handleResponse" />
          </div>
        </div>
        <div class="column is-6 is-fullheight">
          <InvoiceTable />
        </div>
      </div>
    </div>

    <!-- Fullscreen Loader Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-content">
        <p class="loading-text">Uploading and parsing your CSV files...</p>
        <Vue3Lottie
          animationLink="https://assets9.lottiefiles.com/packages/lf20_kdx6cani.json"
          style="width: 150px; height: 150px;"
          :loop="true"
          :autoplay="true"
        />
      </div>
    </div>

    <!-- Modal for API Response -->
    <div v-if="showModal" class="modal is-active">
      <div class="modal-background" @click="closeModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Processing Response</p>
          <button class="delete" aria-label="close" @click="closeModal"></button>
        </header>

        <section class="modal-card-body">
          <div v-if="responseData">
            <!-- Detailed Response when a status exists -->
            <template v-if="responseData.status">
              <p class="has-text-weight-bold is-small">
                Status: {{ responseData.status }}
              </p>
              <div v-if="responseData.Invoice_Numbers && responseData.Invoice_Numbers.length > 0">
                <h2 class="subtitle is-small">✅ Accepted Invoices</h2>
                <ul>
                  <li v-for="invoice in responseData.Invoice_Numbers" :key="invoice">
                    📄 {{ invoice }}
                  </li>
                </ul>
              </div>
              <div v-if="responseData.errors && responseData.errors.length > 0">
                <h2 class="subtitle is-small">❌ Failed Invoices</h2>
                <!-- If errors array contains simple strings -->
                <template v-if="typeof responseData.errors[0] === 'string'">
                  <p>{{ responseData.errors.join(', ') }}</p>
                </template>
                <!-- Otherwise, assume detailed error objects -->
                <template v-else>
                  <table class="table is-fullwidth is-striped is-small">
                    <thead>
                      <tr>
                        <th>Invoice Number</th>
                        <th>Error Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="error in responseData.errors" :key="error.invoice_number">
                        <td>{{ error.invoice_number }}</td>
                        <td>
                          <ul>
                            <li
                              v-for="errDetail in (error.error.errors instanceof Array ? error.error.errors : [])"
                              :key="errDetail.invoice_number"
                            >
                              <strong>{{ errDetail.type }}</strong>:
                              <!-- If details is a string, display it directly -->
                              <span v-if="typeof errDetail.details === 'string'">
                                {{ errDetail.details }}
                              </span>
                              <!-- Otherwise, format as object -->
                              <span v-else>
                                {{ formatErrorDetails(errDetail.details) }}
                              </span>
                            </li>
                          </ul>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </template>
              </div>
            </template>
            <!-- Simple Error Response if no status exists -->
            <template v-else>
              <p class="has-text-weight-bold is-small">
                Error: 
                <span v-if="responseData.errors">
                  {{ typeof responseData.errors[0] === 'string' ? responseData.errors.join(', ') : 'Processing Error' }}
                </span>
                <span v-else>Processing Error</span>
              </p>
            </template>
          </div>
        </section>

        <footer class="modal-card-foot">
          <button class="button is-danger" @click="closeModal">Close</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import CreateOrEditInvoice from '@/components/NewDocument/CreateOrEditInvoice.vue';
import UploadCSV from '@/components/NewDocument/UploadCSV.vue';
import InvoiceTable from '@/components/NewDocument/InvoiceTable.vue';
import { Vue3Lottie } from 'vue3-lottie';

export default {
  name: 'NewDocumentPage',
  components: {
    CreateOrEditInvoice,
    UploadCSV,
    InvoiceTable,
    Vue3Lottie,
  },
  data() {
    return {
      isLoading: false,
      showModal: false,
      responseMessage: '',
      responseData: null,
    };
  },
  methods: {
    toggleLoading(loading) {
      this.isLoading = loading;
      if (!loading && this.responseData) {
        this.showModal = true;
      }
    },
    handleResponse(response) {
      // Use try/catch to process the response and fallback to "Processing Error"
      try {
        // If response is a string, attempt to parse it.
        if (typeof response === 'string') {
          this.responseData = JSON.parse(response);
        } else {
          this.responseData = response;
        }
      } catch (error) {
        this.responseData = { status: "Processing Error" };
      }
      this.showModal = true;
    },
    formatErrorDetails(details) {
      // Assumes details is an object where each property is an array of messages.
      return Object.entries(details)
        .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
        .join('; ');
    },
    closeModal() {
      this.showModal = false;
    },
  },
};
</script>

<style scoped>
.new-document-page {
  min-width: 900px;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.left-half {
  width: 60vh;
  overflow: auto;
  height: 35vh;
}

.column.is-fullheight {
  height: 80vh;
  display: flex;
  flex-direction: column;
}

.invoice-table {
  flex: 1;
  overflow-y: auto;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  z-index: 9999;
}

.loading-content {
  text-align: center;
}

.loading-text {
  font-size: 1.8rem;
  font-weight: bold;
  margin-bottom: 15px;
  color: #110a7a;
}

.modal-card {
  max-width: 600px;
  width: 90%;
}
</style>
